import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { bjToast, Modal } from '@components/common';
import { GooglePlacesInput, Label } from '@components/common/Forms';
import { FooterButtonsSaveDelete } from '@components/templates/profile/common/FooterButtonsSaveDelete';
import { useBjForm } from '@hooks/useBjForm';
import { useSwrResumePreferences } from '@hooks/useSwrApi/endpoints/resume';
import { ApiUserJobTypePreferencesOk, isApiV1Errors } from '@utils/data-fetching';

interface MissingLocationModalProps {
  locale: Locale;
  /** Fired after the address is saved — typically retries the action that triggered the modal. */
  onUpdateSuccess?: () => void;
  /** Fired when the user already has an address on file, so the caller can chain a fallback modal. */
  onNotNeeded?: () => void;
}

/**
 * Address-only modal triggered by apply error 453 when the user's resume location is
 * missing. Saves via /v1/resume/preferences (owner of User.location since API v1.2.16),
 * overlaying the new resumeAddress onto existing preferences so other fields aren't
 * clobbered. Self-closes via {@link onNotNeeded} if preferences already contain an address.
 */
export const MissingLocationModal = NiceModal.create((props: MissingLocationModalProps) => {
  const { locale, onUpdateSuccess, onNotNeeded } = props;
  const { t } = useTranslation('common');
  const modal = useModal();

  const {
    data: preferences,
    mutate: refreshPreferences,
    isLoading,
  } = useSwrResumePreferences({ locale });

  const {
    record, checkFormValidation, postFormData, formStatus, isPosting,
  } = useBjForm({ submitURL: '/v1/resume/preferences' });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [didFallThrough, setDidFallThrough] = useState(false);
  // One-shot guard: only inspect `preferences.resumeAddress` once after the initial
  // SWR load. Without this, a successful save would refresh preferences, flip the
  // flag to true, and re-trigger `onNotNeeded` (opening PersonalDataModal even when
  // it's not needed). NiceModal keeps this component mounted across hide/show, so
  // the effect would otherwise keep watching `preferences`.
  const fallThroughDecidedRef = useRef(false);

  // Use remove() over hide() so the component unmounts; next apply gets a fresh instance
  // with fresh state (the one-shot ref-guard above would otherwise stick across reopens).
  const closeModal = () => { void modal.remove(); };

  // Fall through to the caller (e.g. PersonalDataModal) when address is already on file
  // *at the moment the modal opens*. Subsequent writes from this modal are ignored.
  useEffect(() => {
    if (isLoading || fallThroughDecidedRef.current) return;
    fallThroughDecidedRef.current = true;
    if (preferences?.resumeAddress) {
      if (onNotNeeded) onNotNeeded();
      void modal.remove();
      setDidFallThrough(true);
    }
  }, [isLoading, preferences, modal, onNotNeeded]);

  // Handle save outcome.
  useEffect(() => {
    if (!formSubmitted || isPosting) return;
    if (formStatus.actionStatus === 'success') {
      if (onUpdateSuccess) onUpdateSuccess();
      void refreshPreferences();
      bjToast.success(t('global.action.save_successful'));
      setFormSubmitted(false);
      closeModal();
    }
    if (formStatus.actionStatus === 'error' && isApiV1Errors(formStatus.data)) {
      bjToast.error(formStatus.data.errors[0].message);
      setFormSubmitted(false);
    }
  }, [formStatus, onUpdateSuccess, refreshPreferences, t, formSubmitted, isPosting]);

  if (didFallThrough) return null;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = checkFormValidation(e);
    if (typeof formData !== 'object') {
      // HTML5 validation failed (e.g. empty required field). ComboBox already renders
      // its inline error; surface a toast too so a stuck save button is never silent.
      bjToast.error(t('error.message.empty.location'));
      return;
    }
    const address = (formData as { resumeAddress?: string }).resumeAddress?.trim();
    if (!address) {
      bjToast.error(t('error.message.empty.location'));
      return;
    }

    const body: ApiUserJobTypePreferencesOk = {
      ...(preferences ?? {}),
      resumeAddress: address,
    };
    postFormData('/v1/resume/preferences', body);
    setFormSubmitted(true);
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {}}
      className="!overflow-visible"
    >
      <Modal.Header content={t('post.application.no.details.question')} onClose={closeModal} />
      <Modal.Body className="bg-surface-50">
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-3">
          <Label required htmlFor="resumeAddress" description={t('label.desired.job.address')}>
            <GooglePlacesInput
              record={record}
              recordName="resumeAddress"
              customValidityMessages={{ valueMissing: t('error.message.empty.location') }}
            />
          </Label>
          <FooterButtonsSaveDelete isSaveLoading={isPosting} className="z-1 border-none" />
        </form>
      </Modal.Body>
    </Modal>
  );
});
